import React, { useEffect, useState } from 'react';
import { collection, query, orderBy, getDocs, Timestamp } from 'firebase/firestore';
import { db } from '@/firebase';
import { Zap, ChevronLeft, History, Loader2 } from 'lucide-react';
import { useNavigate } from 'react-router-dom';

/**
 * 📝 ChangeEntry Interface
 * Strictly typed to match the Firestore 'changelog' collection.
 */
interface ChangeEntry {
  id: string;
  version: string;
  title: string;
  date: any; 
  type: 'feature' | 'fix' | 'improvement';
  description: string;
  changes: string[];
}

export const Changelog: React.FC = () => {
  const [logs, setLogs] = useState<ChangeEntry[]>([]);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  useEffect(() => {
    const fetchLogs = async () => {
      try {
        const q = query(collection(db, "changelog"), orderBy("date", "desc"));
        const snap = await getDocs(q);
        
        const data = snap.docs.map(doc => {
          const rawData = doc.data();
          
          // 🛡️ Robust Date Formatting
          let formattedDate = 'Recent';
          if (rawData.date instanceof Timestamp) {
            formattedDate = rawData.date.toDate().toLocaleDateString();
          } else if (rawData.date instanceof Date) {
            formattedDate = rawData.date.toLocaleDateString();
          } else if (typeof rawData.date === 'string') {
            formattedDate = rawData.date;
          }

          return { 
            id: doc.id, 
            ...rawData,
            date: formattedDate
          } as ChangeEntry;
        });
        
        setLogs(data);
      } catch (error) {
        console.error("Failed to sync protocol logs:", error);
      } finally {
        setLoading(false);
      }
    };
    fetchLogs();
  }, []);

  const getTypeStyles = (type: string) => {
    const t = type?.toLowerCase();
    switch (t) {
      case 'feature': return 'bg-emerald-100 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-400';
      case 'fix': return 'bg-rose-100 text-rose-700 dark:bg-rose-900/30 dark:text-rose-400';
      default: return 'bg-indigo-100 text-indigo-700 dark:bg-indigo-900/30 dark:text-indigo-400';
    }
  };

  return (
    <div className="max-w-3xl mx-auto p-6 pb-20 animate-in fade-in duration-700">
      <button 
        onClick={() => navigate(-1)}
        className="flex items-center gap-2 text-slate-400 hover:text-indigo-500 mb-8 transition-colors group"
      >
        <ChevronLeft className="w-4 h-4 group-hover:-translate-x-1 transition-transform" />
        <span className="text-xs font-black uppercase tracking-widest">Back to OS</span>
      </button>

      <div className="flex items-center gap-4 mb-12">
        <div className="p-4 bg-slate-900 dark:bg-white text-white dark:text-slate-900 rounded-[1.5rem] shadow-xl">
          <History className="w-8 h-8" />
        </div>
        <div>
          <h1 className="text-4xl font-black text-slate-900 dark:text-white tracking-tight">Protocol History</h1>
          <p className="text-slate-500 font-medium">Tracking the evolution of EverydayOS</p>
        </div>
      </div>

      <div className="relative space-y-12">
        {/* The Timeline Vertical Rail */}
        <div className="absolute left-6 top-2 bottom-0 w-0.5 bg-slate-100 dark:bg-slate-800" />

        {loading ? (
          <div className="pl-16 space-y-4">
            <div className="h-32 w-full flex items-center justify-center">
               <Loader2 className="w-8 h-8 animate-spin text-slate-200" />
            </div>
          </div>
        ) : logs.map((log) => (
          <div key={log.id} className="relative pl-16">
            <div className="absolute left-[21px] top-2 w-2.5 h-2.5 rounded-full bg-slate-300 dark:bg-slate-700 border-4 border-white dark:border-slate-950 ring-4 ring-slate-50 dark:ring-slate-900/50" />
            
            <div className="bg-white dark:bg-slate-900 rounded-[2rem] border border-slate-100 dark:border-slate-800 p-8 shadow-sm hover:shadow-md transition-all">
              <div className="flex flex-wrap items-center justify-between gap-4 mb-4">
                <div className="flex items-center gap-3">
                  <span className="text-sm font-black text-indigo-600 bg-indigo-50 dark:bg-indigo-900/20 px-3 py-1 rounded-lg">
                    v{log.version}
                  </span>
                  <span className={`text-[10px] font-black uppercase tracking-widest px-3 py-1 rounded-lg ${getTypeStyles(log.type)}`}>
                    {log.type ?? 'UPDATE'}
                  </span>
                </div>
                <time className="text-xs font-bold text-slate-400">{log.date}</time>
              </div>

              <h2 className="text-2xl font-black text-slate-900 dark:text-white mb-2">{log.title}</h2>
              <p className="text-slate-500 dark:text-slate-400 mb-6 leading-relaxed border-b border-slate-50 dark:border-slate-800 pb-6">
                {log.description}
              </p>

              <ul className="space-y-3">
                {(log.changes ?? []).map((change, i) => {
                  // Detect if line is a Category Header (starts with an emoji/non-ASCII)
                  const isHeader = /^[^\x00-\x7F]/.test(change);

                  return (
                    <li 
                      key={i} 
                      className={`flex items-start gap-3 transition-all ${
                        isHeader 
                          ? 'mt-8 mb-2 border-b border-slate-50 dark:border-slate-800 pb-2' 
                          : 'ml-4 opacity-80'
                      }`}
                    >
                      {isHeader ? (
                        <span className="font-black text-slate-900 dark:text-white text-[11px] tracking-[0.2em] uppercase">
                          {change}
                        </span>
                      ) : (
                        <>
                          <Zap className="w-3.5 h-3.5 text-amber-500 shrink-0 mt-1 shadow-sm" />
                          <span className="text-sm font-medium text-slate-600 dark:text-slate-300 leading-relaxed">
                            {change.replace('• ', '').replace('•', '')}
                          </span>
                        </>
                      )}
                    </li>
                  );
                })}
                {(!log.changes || log.changes.length === 0) && (
                  <li className="text-xs italic text-slate-400 tracking-tight">
                    No specific sub-protocols listed for this version.
                  </li>
                )}
              </ul>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};